import { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  CircularProgress,
  Tooltip,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Autocomplete,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import {
  Close as CloseIcon,
  AutoFixHigh as AutoFixHighIcon,
  Assignment as AssignmentIcon,
  Public as PublicIcon,
  Settings as SettingsIcon,
  AccountTree as AccountTreeIcon,
} from "@material-ui/icons";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { tiposApi } from "src/services/tiposApi";
import { sucursalesApi } from "src/services/sucursalesApi";
import { empleadosApi } from "src/services/empleadosApi";
import { AlertaModalV2 } from "../AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { CATEGORIAS } from "src/constants";
import { BRAND } from "src/theme/brandTokens";
// ─── Constantes ──────────────────────────────────────────────────────────────

// Array vacio estable: evita referencias nuevas del default `= []` de RTK Query.
const EMPTY_ARRAY = [];

// ─── Estilos ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    backgroundColor: BRAND.color.primary,
    color: "#fff",
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  closeButton: {
    color: "#fff",
    padding: theme.spacing(0.5),
  },
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  sectionCard: {
    marginBottom: theme.spacing(2.5),
    borderRadius: theme.spacing(1),
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${BRAND.color.primary}`,
  },
  sectionIcon: {
    color: BRAND.color.primary,
    fontSize: "1.1rem",
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  aiCard: {
    background: "linear-gradient(135deg, #fff8ef 0%, #fff3e0 100%)",
    border: `1px solid ${BRAND.color.primary}30`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    boxShadow: "0 1px 4px rgba(255,149,0,0.1)",
  },
  aiHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  aiTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  aiHint: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: 2,
  },
  aiButton: {
    background: `linear-gradient(135deg, ${BRAND.color.primary} 0%, ${BRAND.color.primaryDark} 100%)`,
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    "&:hover": {
      background: `linear-gradient(135deg, ${BRAND.color.primary} 0%,  ${BRAND.color.primaryDark} 100%)`,
    },
    "&:disabled": {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  activeChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  inactiveChip: {
    backgroundColor: "#fafafa",
    color: "#9e9e9e",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  statusBox: {
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    height: 40,
    cursor: "pointer",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(1),
  },
  cancelButton: {
    textTransform: "none",
    fontWeight: 500,
  },
  saveButton: {
    textTransform: "none",
    fontWeight: 600,
    minWidth: 130,
  },
}));

// ─── Validación ───────────────────────────────────────────────────────────────

const validationSchema = Yup.object().shape({
  nombreTarea: Yup.string().required("Nombre de la tarea requerido"),
  valorTarea: Yup.number()
    .typeError("Debe ser un número")
    .min(0, "El valor no puede ser negativo")
    .required("Valor requerido"),
  tipoMoneda: Yup.string().required("Selecciona el tipo de moneda"),
  tipoTarea: Yup.string().required("Selecciona el tipo de tarea"),
  descripcion: Yup.string().required("Descripción interna requerida"),
});

// ─── Subcomponente: encabezado de sección ─────────────────────────────────────

function SectionHeader({ icon: Icon, title, classes }) {
  return (
    <Box className={classes.sectionHeader}>
      <Icon className={classes.sectionIcon} />
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        {title}
      </Typography>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function AgregarTarea({ open, setOpen, monedas }) {
  const classes = useStyles();
  const [generando, setGenerando] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [agregarTipoTarea] = tiposApi.useAddTipoTareasMutation();
  const [generarDescripcion] = tiposApi.useGenerarDescripcionServicioMutation();

  // Fuentes de datos para la derivacion por defecto.
  const { data: todasSucursales = EMPTY_ARRAY } =
    sucursalesApi.useGetSucursalesQuery();
  const [triggerEmpleados, { data: empleados = EMPTY_ARRAY }] =
    empleadosApi.useLazyGetEmpleadosActivosQuery();

  const formik = useFormik({
    initialValues: {
      nombreTarea: "",
      valorTarea: 0,
      tipoMoneda: "",
      tipoTarea: "",
      descripcion: "",
      categoria: "",
      descripcionPublica: "",
      objetivo: "",
      alcance: "",
      entregable: "",
      duracionEstimada: "",
      sucursalDefault: "",
      empleadoEncargadoDefault: "",
      supervisorDefault: "",
      bloquearAsignacion: false,
      activo: true,
      orden: 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      const result = await AlertaModalV2({
        title: "¿Agregar esta tarea?",
        text: `Se creará la tarea "${values.nombreTarea.toUpperCase()}".`,
        confirmButtonText: "Sí, agregar",
        icon: "question",
      }).fire();

      if (!result.isConfirmed) return;

      let valorFinal = values.valorTarea;
      if (String(valorFinal).includes(",")) {
        valorFinal = parseFloat(String(valorFinal).replaceAll(",", "."));
      }

      try {
        await agregarTipoTarea({
          nombre: values.nombreTarea.toUpperCase(),
          valor: valorFinal,
          tipoMoneda: values.tipoMoneda,
          tipoTarea: values.tipoTarea.toUpperCase(),
          descripcion: values.descripcion,
          categoria: values.categoria || undefined,
          descripcionPublica: values.descripcionPublica || undefined,
          objetivo: values.objetivo || undefined,
          alcance: values.alcance || undefined,
          entregable: values.entregable || undefined,
          duracionEstimada: values.duracionEstimada || undefined,
          sucursalDefault: values.sucursalDefault || undefined,
          empleadoEncargadoDefault:
            values.empleadoEncargadoDefault || undefined,
          supervisorDefault: values.supervisorDefault || undefined,
          bloquearAsignacion: values.bloquearAsignacion ? 1 : 0,
          activo: values.activo ? 1 : 0,
          orden: values.orden,
        }).unwrap();

        enqueueSnackbar("Tarea agregada correctamente", { variant: "success" });
        handleCerrar();
      } catch {
        enqueueSnackbar("Error al agregar la tarea. Intenta nuevamente.", {
          variant: "error",
        });
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    resetForm,
    isSubmitting,
  } = formik;

  // Carga los empleados de la sucursal elegida para encargado/supervisor.
  useEffect(() => {
    if (values.sucursalDefault) {
      triggerEmpleados({ id: values.sucursalDefault, estado: "ALL" });
    }
  }, [values.sucursalDefault, triggerEmpleados]);

  const empleadoLabel = (emp) =>
    emp ? `${emp.nombre ?? ""} ${emp.apellidos ?? ""}`.trim() : "";

  const empleadoEncargadoSel =
    empleados.find((e) => e.rut === values.empleadoEncargadoDefault) ?? null;
  const supervisorSel =
    empleados.find((e) => e.rut === values.supervisorDefault) ?? null;

  const handleCerrar = () => {
    resetForm();
    setOpen(false);
  };

  const handleGenerarConIA = async () => {
    if (!values.nombreTarea.trim()) {
      enqueueSnackbar("Ingresa el nombre de la tarea antes de generar con IA", {
        variant: "warning",
      });
      return;
    }
    setGenerando(true);
    try {
      const resultado = await generarDescripcion({
        nombre: values.nombreTarea,
        descripcionInterna: values.descripcion || undefined,
        tipoServicio: "TAREA",
      }).unwrap();
      setFieldValue("descripcionPublica", resultado.descripcionPublica);
      setFieldValue("objetivo", resultado.objetivo);
      setFieldValue("alcance", resultado.alcance);
      setFieldValue("entregable", resultado.entregable);
      setFieldValue("duracionEstimada", resultado.duracionEstimada);
      enqueueSnackbar("Descripción generada con IA", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al generar la descripción con IA", {
        variant: "error",
      });
    } finally {
      setGenerando(false);
    }
  };

  return (
    // disableEnforceFocus: evita el conflicto de aria-hidden entre MUI Dialog y SweetAlert2.
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      aria-labelledby="agregar-tarea-title"
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <DialogTitle className={classes.dialogTitle} id="agregar-tarea-title">
        <Box className={classes.titleContent}>
          <AssignmentIcon />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Nueva Tarea de Servicio
          </Typography>
        </Box>
        <IconButton
          className={classes.closeButton}
          onClick={handleCerrar}
          size="small"
          aria-label="Cerrar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogContent className={classes.dialogContent}>
            {/* ── Sección 1: Datos de la tarea ──────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={AssignmentIcon}
                  title="Datos de la tarea"
                  classes={classes}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nombre de la tarea *"
                      fullWidth
                      autoFocus
                      variant="outlined"
                      size="small"
                      {...getFieldProps("nombreTarea")}
                      error={Boolean(touched.nombreTarea && errors.nombreTarea)}
                      helperText={
                        (touched.nombreTarea && errors.nombreTarea) ||
                        "Se guardará en mayúsculas"
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Valor *"
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      {...getFieldProps("valorTarea")}
                      error={Boolean(touched.valorTarea && errors.valorTarea)}
                      helperText={touched.valorTarea && errors.valorTarea}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={Boolean(touched.tipoMoneda && errors.tipoMoneda)}
                    >
                      <InputLabel>Moneda *</InputLabel>
                      <Select {...getFieldProps("tipoMoneda")} label="Moneda *">
                        <MenuItem value="">
                          <em>Seleccionar</em>
                        </MenuItem>
                        {monedas.map((m, i) => (
                          <MenuItem key={i} value={m.nombre}>
                            {m.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.tipoMoneda && errors.tipoMoneda && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ marginLeft: 14, marginTop: 3 }}
                        >
                          {errors.tipoMoneda}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={Boolean(touched.tipoTarea && errors.tipoTarea)}
                    >
                      <InputLabel>Tipo de tarea *</InputLabel>
                      <Select
                        {...getFieldProps("tipoTarea")}
                        label="Tipo de tarea *"
                      >
                        <MenuItem value="">
                          <em>Seleccionar</em>
                        </MenuItem>
                        <MenuItem value="ESPECIAL">ESPECIAL</MenuItem>
                        <MenuItem value="NORMAL">NORMAL</MenuItem>
                      </Select>
                      {touched.tipoTarea && errors.tipoTarea && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ marginLeft: 14, marginTop: 3 }}
                        >
                          {errors.tipoTarea}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Categoría</InputLabel>
                      <Select {...getFieldProps("categoria")} label="Categoría">
                        <MenuItem value="">
                          <em>Sin categoría</em>
                        </MenuItem>
                        {CATEGORIAS.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    {/* spacer — mantiene alineación en pantallas grandes */}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Descripción interna *"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      {...getFieldProps("descripcion")}
                      error={Boolean(touched.descripcion && errors.descripcion)}
                      helperText={
                        (touched.descripcion && errors.descripcion) ||
                        "Visible solo para el equipo interno"
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 2: Configuración ──────────────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={SettingsIcon}
                  title="Configuración"
                  classes={classes}
                />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Orden de visualización"
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0 }}
                      helperText="Menor número = aparece primero"
                      {...getFieldProps("orden")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box className={classes.statusBox}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.activo}
                            onChange={(e) =>
                              setFieldValue("activo", e.target.checked)
                            }
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Chip
                            label={values.activo ? "Activo" : "Inactivo"}
                            size="small"
                            className={
                              values.activo
                                ? classes.activeChip
                                : classes.inactiveChip
                            }
                          />
                        }
                        style={{ margin: 0 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 2b: Derivación por defecto ────────────────────────── */}
            <Card className={classes.sectionCard}>
              <CardContent>
                <SectionHeader
                  icon={AccountTreeIcon}
                  title="Derivación por defecto (opcional)"
                  classes={classes}
                />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={todasSucursales}
                      getOptionLabel={(option) => option.nombre ?? option}
                      isOptionEqualToValue={(option, value) =>
                        (option.nombre ?? option) === (value.nombre ?? value)
                      }
                      value={
                        todasSucursales.find(
                          (s) => s.nombre === values.sucursalDefault,
                        ) ?? null
                      }
                      onChange={(_, newValue) => {
                        setFieldValue(
                          "sucursalDefault",
                          newValue ? newValue.nombre : "",
                        );
                        // Reinicia encargado/supervisor al cambiar de sucursal.
                        setFieldValue("empleadoEncargadoDefault", "");
                        setFieldValue("supervisorDefault", "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Sucursal por defecto"
                          placeholder="Seleccionar sucursal..."
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={empleados}
                      disabled={!values.sucursalDefault}
                      getOptionLabel={(option) => empleadoLabel(option)}
                      isOptionEqualToValue={(option, value) =>
                        option.rut === value.rut
                      }
                      value={empleadoEncargadoSel}
                      onChange={(_, newValue) =>
                        setFieldValue(
                          "empleadoEncargadoDefault",
                          newValue ? newValue.rut : "",
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Encargado por defecto"
                          placeholder={
                            values.sucursalDefault
                              ? "Seleccionar encargado..."
                              : "Selecciona primero una sucursal"
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={empleados}
                      disabled={!values.sucursalDefault}
                      getOptionLabel={(option) => empleadoLabel(option)}
                      isOptionEqualToValue={(option, value) =>
                        option.rut === value.rut
                      }
                      value={supervisorSel}
                      onChange={(_, newValue) =>
                        setFieldValue(
                          "supervisorDefault",
                          newValue ? newValue.rut : "",
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Supervisor por defecto"
                          placeholder={
                            values.sucursalDefault
                              ? "Seleccionar supervisor..."
                              : "Selecciona primero una sucursal"
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.bloquearAsignacion}
                          onChange={(e) =>
                            setFieldValue(
                              "bloquearAsignacion",
                              e.target.checked,
                            )
                          }
                          color="primary"
                          size="small"
                        />
                      }
                      label="Bloquear asignación en la propuesta"
                      style={{ margin: 0 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ── Sección 3: Información pública + IA ──────────────────────── */}
            <Card className={classes.aiCard}>
              <CardContent>
                <Box className={classes.aiHeader}>
                  <Box className={classes.aiTitleGroup}>
                    <PublicIcon className={classes.sectionIcon} />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        className={classes.sectionTitle}
                      >
                        Información pública del servicio
                      </Typography>
                      <Typography className={classes.aiHint}>
                        Visible para el cliente en propuestas comerciales
                      </Typography>
                    </Box>
                  </Box>

                  <Tooltip
                    title={
                      !values.nombreTarea.trim()
                        ? "Ingresa el nombre de la tarea para habilitar"
                        : "Genera objetivo, alcance, entregable y duración automáticamente con IA"
                    }
                    placement="left"
                  >
                    <span>
                      <Button
                        className={classes.aiButton}
                        onClick={handleGenerarConIA}
                        disabled={generando || !values.nombreTarea.trim()}
                        startIcon={
                          generando ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <AutoFixHighIcon />
                          )
                        }
                        size="small"
                      >
                        {generando ? "Generando..." : "Generar con IA"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Descripción pública"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="Describe el servicio en términos que el cliente pueda entender..."
                      helperText={`${(values.descripcionPublica || "").length} caracteres`}
                      {...getFieldProps("descripcionPublica")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Objetivo"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="¿Cuál es el propósito de esta tarea?"
                      {...getFieldProps("objetivo")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Alcance"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="¿Qué incluye esta tarea?"
                      {...getFieldProps("alcance")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Entregable"
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="¿Qué se entrega al completar esta tarea?"
                      {...getFieldProps("entregable")}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Duración estimada"
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Ej: 3-5 días hábiles"
                      helperText="Tiempo aproximado para completar la tarea"
                      {...getFieldProps("duracionEstimada")}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </DialogContent>

          {/* ── Footer ──────────────────────────────────────────────────────── */}
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={handleCerrar}
              variant="outlined"
              className={classes.cancelButton}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.saveButton}
              disabled={isSubmitting}
              startIcon={
                isSubmitting && <CircularProgress size={16} color="inherit" />
              }
            >
              {isSubmitting ? "Guardando..." : "Agregar Tarea"}
            </Button>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
}
